/**
 * useActiveQuestionTime — per-question ACTIVE-time accumulator for the survey runner.
 *
 * Measures how long the respondent actively worked on the current question. Time is split
 * into an active bucket and an idle bucket; the clock flips to idle when:
 *   - the tab is hidden (visibilitychange -> hidden), or
 *   - the window loses focus (blur), or
 *   - no user input (keydown / pointerdown / throttled pointermove) arrives for
 *     IDLE_TIMEOUT_MS — the untouched 90s themselves still count as active (reading /
 *     thinking time); only time beyond the timeout is idle.
 * It flips back to active on visible / focus / any input. blur_count counts hidden/blur
 * departures (one per departure, however many events fire).
 *
 * resetForQuestion() zeroes the buckets and must be called from the same effects that set
 * questionStartTime; getTimings() snapshots {active_time_ms, idle_time_ms, blur_count} at
 * submit time. Every collection (getTimings or collectDelta) also marks its snapshot as
 * reported, so collectDelta() — used when submitting AI follow-up answers for the SAME
 * still-displayed question — returns only the accrual since the previous submission while
 * measurement keeps running. Fail-open by design: when disabled or on any internal error
 * getTimings()/collectDelta() return null so callers simply omit the fields.
 */
import { useCallback, useEffect, useRef } from 'react';
import type { ActiveTimeMetrics } from '../../utils/surveyApi';

export const IDLE_TIMEOUT_MS = 90_000;
/** pointermove fires continuously; only sample it once per second. */
const POINTERMOVE_THROTTLE_MS = 1_000;

interface ActiveTimeState {
  activeMs: number;
  idleMs: number;
  blurCount: number;
  /** Portion of each bucket already handed to a caller (getTimings / collectDelta). */
  reportedActiveMs: number;
  reportedIdleMs: number;
  reportedBlurCount: number;
  /** When the currently-running segment (active or paused) started. */
  segmentStartMs: number;
  lastInputMs: number;
  paused: boolean;
  hidden: boolean;
  blurred: boolean;
  idle: boolean;
}

const createInitialState = (): ActiveTimeState => ({
  activeMs: 0,
  idleMs: 0,
  blurCount: 0,
  reportedActiveMs: 0,
  reportedIdleMs: 0,
  reportedBlurCount: 0,
  segmentStartMs: Date.now(),
  lastInputMs: Date.now(),
  paused: false,
  hidden: false,
  blurred: false,
  idle: false,
});

/** Bucket totals as of now, including the still-open segment (nothing is closed/mutated). */
const effectiveTotals = (s: ActiveTimeState): { active: number; idle: number } => {
  const elapsed = Math.max(0, Date.now() - s.segmentStartMs);
  return {
    active: s.activeMs + (s.paused ? 0 : elapsed),
    idle: s.idleMs + (s.paused ? elapsed : 0),
  };
};

export interface UseActiveQuestionTimeResult {
  /**
   * Snapshot the timings for the current question; null when disabled or on error.
   * Also marks the snapshot as reported (it accompanies a submission), so a later
   * collectDelta() returns only post-submit accrual. The returned values are always
   * the full per-question totals — unchanged by the marking.
   */
  getTimings: () => ActiveTimeMetrics | null;
  /**
   * Accrual since the previous collection (getTimings at the main-answer submit, or
   * the previous collectDelta), for AI follow-up submissions on the still-displayed
   * question. Marks the collected amount as reported; measurement keeps running.
   * Null when disabled or on error.
   */
  collectDelta: () => ActiveTimeMetrics | null;
  /** Zero the accumulators; call whenever a new question is shown. */
  resetForQuestion: () => void;
}

export function useActiveQuestionTime(enabled: boolean): UseActiveQuestionTimeResult {
  const stateRef = useRef<ActiveTimeState | null>(null);
  if (stateRef.current === null) stateRef.current = createInitialState();
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  /** Close the running segment into the right bucket and restart it at `now`. */
  const rollSegment = useCallback((now: number) => {
    const s = stateRef.current!;
    const elapsed = Math.max(0, now - s.segmentStartMs);
    if (s.paused) s.idleMs += elapsed;
    else s.activeMs += elapsed;
    s.segmentStartMs = now;
  }, []);

  /** Recompute paused from the pause causes, rolling the segment on a transition. */
  const syncPaused = useCallback(
    (now: number) => {
      const s = stateRef.current!;
      const paused = s.hidden || s.blurred || s.idle;
      if (paused === s.paused) return;
      rollSegment(now);
      s.paused = paused;
    },
    [rollSegment],
  );

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current !== null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  /**
   * Arm the idle watchdog. Check-on-fire pattern: the timer verifies elapsed input
   * silence when it fires and re-arms for the remainder, so per-keystroke input does
   * not need to clear/reset a timeout.
   */
  const scheduleIdleCheck = useCallback(() => {
    clearIdleTimer();
    const check = () => {
      idleTimerRef.current = null;
      const s = stateRef.current!;
      const now = Date.now();
      const sinceInput = now - s.lastInputMs;
      if (sinceInput >= IDLE_TIMEOUT_MS) {
        s.idle = true;
        syncPaused(now);
      } else {
        idleTimerRef.current = setTimeout(check, IDLE_TIMEOUT_MS - sinceInput);
      }
    };
    idleTimerRef.current = setTimeout(check, IDLE_TIMEOUT_MS);
  }, [clearIdleTimer, syncPaused]);

  const markInput = useCallback(
    (now: number) => {
      const s = stateRef.current!;
      s.lastInputMs = now;
      if (s.idle) {
        s.idle = false;
        syncPaused(now);
        scheduleIdleCheck();
      }
    },
    [syncPaused, scheduleIdleCheck],
  );

  /** Apply a visibility/focus change; counts departures and restarts the idle window on return. */
  const setAwayState = useCallback(
    (partial: { hidden?: boolean; blurred?: boolean }) => {
      const s = stateRef.current!;
      const now = Date.now();
      const wasAway = s.hidden || s.blurred;
      if (partial.hidden !== undefined) s.hidden = partial.hidden;
      if (partial.blurred !== undefined) s.blurred = partial.blurred;
      const isAway = s.hidden || s.blurred;
      if (!wasAway && isAway) s.blurCount += 1;
      if (wasAway && !isAway) {
        // Returning counts as fresh engagement: clear idle and restart its window.
        s.idle = false;
        s.lastInputMs = now;
        scheduleIdleCheck();
      }
      syncPaused(now);
    },
    [syncPaused, scheduleIdleCheck],
  );

  useEffect(() => {
    if (!enabled) return;
    try {
      const s = stateRef.current!;
      const now = Date.now();
      s.segmentStartMs = now;
      s.lastInputMs = now;
      s.hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
      // A survey opened in a visible-but-unfocused window must not accrue
      // active time until it actually gains focus.
      s.blurred =
        typeof document !== 'undefined' && typeof document.hasFocus === 'function'
          ? !document.hasFocus()
          : s.blurred;
      s.paused = s.hidden || s.blurred || s.idle;

      const onVisibility = () => setAwayState({ hidden: document.visibilityState === 'hidden' });
      const onBlur = () => setAwayState({ blurred: true });
      const onFocus = () => setAwayState({ blurred: false });
      const onInput = () => markInput(Date.now());
      const onPointerMove = () => {
        const moveNow = Date.now();
        if (moveNow - stateRef.current!.lastInputMs < POINTERMOVE_THROTTLE_MS) return;
        markInput(moveNow);
      };

      document.addEventListener('visibilitychange', onVisibility);
      window.addEventListener('blur', onBlur);
      window.addEventListener('focus', onFocus);
      window.addEventListener('keydown', onInput, true);
      window.addEventListener('pointerdown', onInput, true);
      window.addEventListener('pointermove', onPointerMove, { capture: true, passive: true });
      window.addEventListener('wheel', onPointerMove, { capture: true, passive: true });

      scheduleIdleCheck();

      return () => {
        document.removeEventListener('visibilitychange', onVisibility);
        window.removeEventListener('blur', onBlur);
        window.removeEventListener('focus', onFocus);
        window.removeEventListener('keydown', onInput, true);
        window.removeEventListener('pointerdown', onInput, true);
        window.removeEventListener('pointermove', onPointerMove, { capture: true });
        window.removeEventListener('wheel', onPointerMove, { capture: true });
        clearIdleTimer();
      };
    } catch {
      // Fail-open: tracking stays off, the survey itself is unaffected.
      return undefined;
    }
  }, [enabled, setAwayState, markInput, scheduleIdleCheck, clearIdleTimer]);

  const resetForQuestion = useCallback(() => {
    try {
      const s = stateRef.current!;
      const now = Date.now();
      s.activeMs = 0;
      s.idleMs = 0;
      s.blurCount = 0;
      s.reportedActiveMs = 0;
      s.reportedIdleMs = 0;
      s.reportedBlurCount = 0;
      s.segmentStartMs = now;
      s.lastInputMs = now;
      s.idle = false;
      s.paused = s.hidden || s.blurred;
      if (enabledRef.current) scheduleIdleCheck();
    } catch {
      // Fail-open.
    }
  }, [scheduleIdleCheck]);

  const getTimings = useCallback((): ActiveTimeMetrics | null => {
    if (!enabledRef.current) return null;
    try {
      const s = stateRef.current!;
      const { active, idle } = effectiveTotals(s);
      s.reportedActiveMs = active;
      s.reportedIdleMs = idle;
      s.reportedBlurCount = s.blurCount;
      return {
        active_time_ms: Math.round(active),
        idle_time_ms: Math.round(idle),
        blur_count: s.blurCount,
      };
    } catch {
      return null;
    }
  }, []);

  const collectDelta = useCallback((): ActiveTimeMetrics | null => {
    if (!enabledRef.current) return null;
    try {
      const s = stateRef.current!;
      const { active, idle } = effectiveTotals(s);
      const delta: ActiveTimeMetrics = {
        active_time_ms: Math.max(0, Math.round(active - s.reportedActiveMs)),
        idle_time_ms: Math.max(0, Math.round(idle - s.reportedIdleMs)),
        blur_count: Math.max(0, s.blurCount - s.reportedBlurCount),
      };
      s.reportedActiveMs = active;
      s.reportedIdleMs = idle;
      s.reportedBlurCount = s.blurCount;
      return delta;
    } catch {
      return null;
    }
  }, []);

  return { getTimings, collectDelta, resetForQuestion };
}
